import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Calendar, ArrowLeft } from 'lucide-react';
import reportService from '../../services/report/reportService';
import apiService from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import CommissionAdjustModal from './CommissionAdjustModal';
import BlotterReportFilters from './BlotterReportFilters';
import BlotterReportResults, { BlotterEmployee } from './BlotterReportResults';
import type { BlotterReportData } from '../../types/report';
import type { ManualCheck } from '../../types/manualCheck';

interface ModalState {
  isOpen: boolean;
  mode: 'move' | 'split';
  transactionId: number;
  grossCommission: number;
  currentUserId?: number;
  currentUserName?: string;
  currentPercentage?: number;
}

const BlotterReport: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [date, setDate] = useState<string>(
    searchParams.get('date') || new Date().toISOString().split('T')[0]
  );
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [reportData, setReportData] = useState<BlotterReportData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedEmployees, setExpandedEmployees] = useState<Set<number>>(new Set());
  const [printingEmployee, setPrintingEmployee] = useState<number | null>(null);
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    mode: 'move',
    transactionId: 0,
    grossCommission: 0,
  });
  const [deletingCommission, setDeletingCommission] = useState<number | null>(null);

  // R17 Task C: manual checks for the selected day surface alongside the
  // blotter response so salesmen with a check but no trades still get a
  // Print PDF button. Merged into `employees` below.
  const [checksForDay, setChecksForDay] = useState<ManualCheck[]>([]);

  const handleGenerateReport = useCallback(async (reportDate?: string, silent = false) => {
    if (silent) {
      setIsRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    const activeDate = reportDate || date;

    // Fetch blotter report + same-day manual checks in parallel. Neither
    // should block the other — an all-checks day resolves the blotter
    // promise with a rejected 404 while the checks promise resolves
    // cleanly, and vice versa.
    const [blotterResult, checksResult] = await Promise.allSettled([
      reportService.generateBlotterReport({ date: activeDate }),
      apiService.getManualChecks({
        start_date: activeDate,
        end_date: activeDate,
        per_page: 100,
      }),
    ]);

    // Manual checks: soft-fail — an error here should not tank the whole
    // report view (the blotter data alone is still useful). Log but keep
    // the array empty and continue.
    if (checksResult.status === 'fulfilled') {
      setChecksForDay(checksResult.value.data || []);
    } else {
      // eslint-disable-next-line no-console
      console.warn('Failed to load manual checks for date', activeDate, checksResult.reason);
      setChecksForDay([]);
    }

    if (blotterResult.status === 'fulfilled') {
      setReportData(blotterResult.value.data);
    } else {
      const err = blotterResult.reason;
      const status = err?.response?.status;
      const checkCount =
        checksResult.status === 'fulfilled' ? (checksResult.value.data?.length ?? 0) : 0;

      // Peter's #10 fix: a 404 "no blotter" is NOT an error when the day
      // has manual checks — synthesize an empty blotter shell so the
      // check-only employees can still render with a Print PDF button.
      if (status === 404 && checkCount > 0) {
        setReportData({
          blotter: { id: 0, date: activeDate, filename: null },
          summary: {
            gross_commission: 0,
            salesman_commission: 0,
            house_commission: 0,
            transaction_count: 0,
          },
          accounts: [],
          employees: [],
        });
      } else {
        setReportData(null);
        setError(err?.response?.data?.message || 'Failed to generate report');
      }
    }

    setLoading(false);
    setIsRefreshing(false);
  }, [date]);

  // Auto-generate report when date query param is present
  useEffect(() => {
    const queryDate = searchParams.get('date');
    if (queryDate) {
      handleGenerateReport(queryDate);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePrintEmployee = async (employee: BlotterEmployee) => {
    try {
      setPrintingEmployee(employee.user_id);
      // Use consolidated PDF (grouped by client/symbol) as per client request
      await reportService.printEmployeeBlotterConsolidated({ date, user_id: employee.user_id });
    } catch (err: any) {
      console.error('Failed to print employee report:', err);
      setError(err.response?.data?.message || 'Failed to print employee report');
    } finally {
      setPrintingEmployee(null);
    }
  };

  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      await reportService.downloadBlotterExcel({ date });
    } catch (err: any) {
      setError(err.message || 'Failed to download Excel');
    } finally {
      setDownloadingExcel(false);
    }
  };

  const toggleEmployee = (userId: number) => {
    setExpandedEmployees(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const handleOpenModal = (
    mode: 'move' | 'split',
    transactionId: number,
    grossCommission: number,
    currentUserId?: number,
    currentUserName?: string,
    currentPercentage?: number
  ) => {
    setModalState({
      isOpen: true,
      mode,
      transactionId,
      grossCommission,
      currentUserId,
      currentUserName,
      currentPercentage,
    });
  };

  const handleCloseModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  const handleModalSuccess = () => {
    // Silent refresh — keeps scroll position and expanded sections intact
    handleGenerateReport(undefined, true);
  };

  const handleDeleteCommission = async (userCommissionId: number, transactionId: number) => {
    if (!window.confirm('Are you sure you want to delete this commission split?')) {
      return;
    }

    try {
      setDeletingCommission(userCommissionId);
      await apiService.deleteCommissionSplit(userCommissionId);
      handleGenerateReport(undefined, true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete commission split');
    } finally {
      setDeletingCommission(null);
    }
  };

  const handleDownloadBlotterFile = async () => {
    if (!reportData?.blotter?.filename) return;
    try {
      await apiService.downloadFile(
        `/blotter/${reportData.blotter.id}/download`,
        reportData.blotter.filename as string
      );
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to download blotter file'
      );
    }
  };

  // Backend already sorts House to the top, but enforce it on the client too
  // so the UI guarantees the order even if a future backend change ships
  // employees in a different sequence.
  //
  // R17 Task C — merge check-only salesmen: any salesman with manual
  // checks on this date who is NOT already in the blotter employees list
  // (either because there's no blotter at all, or because they had no
  // trades) gets a synthesized row. This row's Print PDF button hits the
  // same consolidated PDF endpoint, which already renders a valid PDF
  // for check-only days (R16 Task A).
  const blotterEmployees: BlotterEmployee[] = (reportData?.employees || []).slice();
  const existingUserIds = new Set(blotterEmployees.map((e) => e.user_id));
  const syntheticBySalesman = new Map<number, BlotterEmployee>();
  for (const check of checksForDay) {
    const uid = check.salesman_user_id;
    if (!uid || existingUserIds.has(uid)) continue;
    const existing = syntheticBySalesman.get(uid);
    const commission = Number(check.salesman_commission) || 0;
    if (existing) {
      existing.total_commission = (existing.total_commission || 0) + commission;
    } else {
      syntheticBySalesman.set(uid, {
        user_id: uid,
        user_name: check.salesman?.name || 'Unknown Salesman',
        user_email: check.salesman?.email || '',
        is_house: false,
        total_gross_commission: 0,
        total_commission: commission,
        transaction_count: 0,
        transactions: [],
        checks_only: true,
      });
    }
  }

  const employees: BlotterEmployee[] = [...blotterEmployees, ...Array.from(syntheticBySalesman.values())]
    .sort((a, b) => {
      // House first, then real-blotter employees, then synthetic check-only.
      if (!!a.is_house !== !!b.is_house) return a.is_house ? -1 : 1;
      if (!!a.checks_only !== !!b.checks_only) return a.checks_only ? 1 : -1;
      return 0;
    });

  return (
    <div className="p-6">
      <button
        onClick={() => navigate('/blotters')}
        className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1 mb-4"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Blotters
      </button>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <Calendar className="h-7 w-7 mr-3 text-primary-600" />
          Blotter Report
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Generate commission report for a specific trading date
        </p>
      </div>

      <BlotterReportFilters
        date={date}
        onDateChange={setDate}
        onGenerate={() => handleGenerateReport()}
        onDownloadExcel={handleDownloadExcel}
        loading={loading}
        downloadingExcel={downloadingExcel}
        /* R17 Task C: Excel export only makes sense when a real CSV
           blotter exists — check-only days synthesize `blotter.id = 0`
           and the export endpoint would 404. Per-employee PDFs stay
           available via the button on each employee card. */
        showExportButtons={!!reportData && reportData.blotter.id > 0}
        error={error}
      />

      {/* Loading State — only shown on initial/manual generate, not on silent refresh */}
      {loading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
          <div className="flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
            <p className="text-gray-600">Generating report...</p>
          </div>
        </div>
      )}

      {/* Silent refresh indicator — stays in corner, no layout shift */}
      {isRefreshing && (
        <div className="fixed bottom-6 right-6 bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg flex items-center text-sm z-40">
          <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
          Updating...
        </div>
      )}

      {/* Report Data — visible during silent refresh so page doesn't jump */}
      {!loading && reportData && (
        <BlotterReportResults
          reportData={reportData}
          employees={employees}
          isAdmin={isAdmin}
          expandedEmployees={expandedEmployees}
          onToggleEmployee={toggleEmployee}
          onPrintEmployee={handlePrintEmployee}
          printingEmployee={printingEmployee}
          onOpenCommissionModal={handleOpenModal}
          onDownloadBlotterFile={handleDownloadBlotterFile}
        />
      )}

      {/* Commission Adjustment Modal */}
      <CommissionAdjustModal
        isOpen={modalState.isOpen}
        onClose={handleCloseModal}
        transactionId={modalState.transactionId}
        grossCommission={modalState.grossCommission}
        mode={modalState.mode}
        currentUserId={modalState.currentUserId}
        currentUserName={modalState.currentUserName}
        currentPercentage={modalState.currentPercentage}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default BlotterReport;
